import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '@/components/ui/Button'
import SEO from '@/components/common/SEO'
import { useSelfAssessmentStore } from '@/stores/selfAssessmentStore'
import { ASSESSMENT_QUESTIONS } from '@/data/selfAssessmentQuestions'
import { trackStartSelfAssessment } from '@/lib/analytics'

const CATEGORY_COLORS: Record<string, string> = {
  '신체기능': 'from-blue-100 to-indigo-100 text-cb-primary',
  '인지기능': 'from-purple-100 to-pink-100 text-purple-600',
  '행동변화': 'from-amber-100 to-orange-100 text-cb-accent',
  '간호처치': 'from-rose-100 to-red-100 text-rose-600',
  '재활': 'from-green-100 to-emerald-100 text-cb-secondary',
}

export default function SelfAssessmentPage() {
  const navigate = useNavigate()
  const { step, answers, setAnswer, nextStep, prevStep, reset, setCompleted } = useSelfAssessmentStore()

  const question = ASSESSMENT_QUESTIONS[step - 1]!
  const currentAnswer = answers[question.id]
  const isLastStep = step === 8
  const progress = (step / 8) * 100

  // 진입 시 항상 리셋 (새로 시작)
  useEffect(() => {
    reset()
    trackStartSelfAssessment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNext = () => {
    if (isLastStep) {
      setCompleted()
      navigate('/guide/self-assessment/result')
    } else {
      nextStep()
    }
  }

  const handleBack = () => {
    if (step === 1) {
      navigate(-1)
    } else {
      prevStep()
    }
  }

  const categoryColor = CATEGORY_COLORS[question.category] || 'from-gray-100 to-gray-200 text-gray-600'

  return (
    <>
      <SEO title="요양등급 자가진단" url="/guide/self-assessment" />

      {/* ===== 데스크톱 트리 (모바일 미노출) ===== */}
      <div className="hidden lg:block">
        <div className="min-h-screen bg-gray-50 flex">
          {/* Left: Info Panel */}
          <div className="w-[400px] flex-shrink-0 bg-gradient-to-br from-[#1e3a8a] via-[#2563eb] to-[#4338ca] p-10 text-white flex flex-col relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
            <button onClick={() => navigate(-1)} className="relative flex items-center gap-2 text-white/80 hover:text-white text-sm mb-10">
              <ArrowLeft size={16} /> 돌아가기
            </button>
            <h1 className="text-2xl font-bold leading-tight">
              요양등급<br />자가진단
            </h1>
            <p className="mt-3 text-sm text-white/70 leading-relaxed">
              8개 문항으로 예상 장기요양등급을 확인해보세요. 약 3분이면 충분합니다.
            </p>

            {/* Step indicators */}
            <div className="mt-10 flex flex-col gap-3">
              {ASSESSMENT_QUESTIONS.map((q, i) => {
                const isActive = i + 1 === step
                const isDone = i + 1 < step
                return (
                  <div key={q.id} className={`flex items-center gap-3 text-sm transition-opacity ${isActive ? 'opacity-100' : isDone ? 'opacity-60' : 'opacity-30'}`}>
                    <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${isActive ? 'bg-white text-cb-primary' : isDone ? 'bg-white/30 text-white' : 'bg-white/10 text-white/50'}`}>
                      {isDone ? '✓' : i + 1}
                    </div>
                    <span className={isActive ? 'font-semibold' : ''}>{q.category} — {q.question.slice(0, 12)}...</span>
                  </div>
                )
              })}
            </div>

            <div className="mt-auto text-xs text-white/40">
              본 자가진단은 참고용이며, 정확한 등급 판정은 국민건강보험공단의 방문조사를 통해 이루어집니다.
            </div>
          </div>

          {/* Right: Question */}
          <div className="flex-1 flex items-center justify-center p-10">
            <div className="w-full max-w-lg">
              <div className="mb-6 flex items-center justify-between text-sm text-gray-500">
                <span className={`rounded-full bg-gradient-to-r ${categoryColor} px-3 py-1 text-xs font-semibold`}>
                  {question.category}
                </span>
                <span>{step} / 8</span>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                >
                  <h2 className="text-xl font-bold text-gray-900 mb-6">{question.question}</h2>
                  <div className="flex flex-col gap-3">
                    {question.options.map((opt) => (
                      <button
                        key={opt.score}
                        onClick={() => setAnswer(question.id, opt.score)}
                        className={`w-full rounded-2xl border-2 p-4 text-left transition-all ${
                          currentAnswer === opt.score
                            ? 'border-cb-primary bg-cb-primary/5 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <p className={`text-sm font-semibold ${currentAnswer === opt.score ? 'text-cb-primary' : 'text-gray-900'}`}>
                          {opt.label}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">{opt.description}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 flex gap-3">
                <Button variant="secondary" size="lg" className="flex-shrink-0" onClick={handleBack}>
                  <ArrowLeft size={16} /> 이전
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={currentAnswer === undefined}
                  onClick={handleNext}
                >
                  {isLastStep ? '결과 확인하기' : '다음'}
                  {!isLastStep && <ArrowRight size={16} />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== 모바일 트리 (데스크톱 미노출) ===== */}
      <div className="lg:hidden">
        <div className="flex min-h-screen flex-col bg-white">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
          <div className="flex items-center gap-3 px-4 py-3">
            <button onClick={handleBack} className="p-1">
              <ArrowLeft size={20} className="text-gray-700" />
            </button>
            <span className="text-base font-semibold text-gray-900">자가진단</span>
            <span className="ml-auto text-sm text-gray-500">{step}/8</span>
          </div>
          {/* Progress bar */}
          <div className="h-1 bg-gray-100">
            <motion.div
              className="h-full bg-cb-primary"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-5 py-6">
          <span className={`inline-block rounded-full bg-gradient-to-r ${categoryColor} px-3 py-1 text-xs font-semibold mb-4`}>
            {question.category}
          </span>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="text-lg font-bold text-gray-900 mb-5">{question.question}</h2>
              <div className="flex flex-col gap-2.5">
                {question.options.map((opt) => (
                  <button
                    key={opt.score}
                    onClick={() => setAnswer(question.id, opt.score)}
                    className={`w-full rounded-xl border-2 p-3.5 text-left transition-all ${
                      currentAnswer === opt.score
                        ? 'border-cb-primary bg-cb-primary/5'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <p className={`text-sm font-semibold ${currentAnswer === opt.score ? 'text-cb-primary' : 'text-gray-900'}`}>
                      {opt.label}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">{opt.description}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom nav */}
        <div className="sticky bottom-0 border-t border-gray-100 bg-white px-5 py-4">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={currentAnswer === undefined}
            onClick={handleNext}
          >
            {isLastStep ? '결과 확인하기' : '다음'}
          </Button>
        </div>
        </div>
      </div>
    </>
  )
}
